import { useEffect, useState, useCallback } from "react";
import { getTodayScintilla } from "@/data/scintille";

const PREF_KEY = "lsw_notifications_enabled";
const LAST_SHOWN_KEY = "lsw_notif_last_shown";

function isSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

function getNext8amMs() {
  const now = new Date();
  const next = new Date(now);
  next.setHours(8, 0, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return next.getTime() - now.getTime();
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function showDailyNotification() {
  if (!isSupported() || Notification.permission !== "granted") return;
  const { quote, author } = getTodayScintilla();
  try {
    new Notification("Scintilla del Giorno · LSW", {
      body: `“${quote.length > 140 ? quote.slice(0, 137) + "…" : quote}” — ${author}`,
      tag: `lsw-daily-${todayKey()}`,
      icon: "/favicon.ico",
    });
    localStorage.setItem(LAST_SHOWN_KEY, todayKey());
  } catch {
    // some browsers throw if not invoked via SW; silent fallback
  }
}

export function useDailyNotifications() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(PREF_KEY) === "1");
  const [permission, setPermission] = useState(() =>
    isSupported() ? Notification.permission : "unsupported"
  );

  const supported = isSupported();

  // Schedule next 8am notification while the app is open
  useEffect(() => {
    if (!enabled || permission !== "granted") return undefined;

    // Catch-up: if it's already past 8 AM today and we haven't shown today's, fire now
    const now = new Date();
    const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
    if (now.getHours() >= 8 && lastShown !== todayKey()) {
      showDailyNotification();
    }

    const delay = getNext8amMs();
    const timer = setTimeout(function fire() {
      showDailyNotification();
    }, delay);
    return () => clearTimeout(timer);
  }, [enabled, permission]);

  const toggle = useCallback(async (next) => {
    if (!supported) return;
    if (next) {
      let perm = Notification.permission;
      if (perm === "default") {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== "granted") return;
      localStorage.setItem(PREF_KEY, "1");
      setEnabled(true);
    } else {
      localStorage.setItem(PREF_KEY, "0");
      setEnabled(false);
    }
  }, [supported]);

  return { enabled, supported, permission, toggle };
}
